import { useContext, useState, useEffect, memo, useRef } from 'react';
import { Centrifuge } from 'centrifuge';
import { useDispatch, useSelector } from 'react-redux';
import { updateSerp } from '@/app/Serp/reducers/serpDataSlice';
import { NewSerpContext } from '@/context/Context';
import { webSocketUrl } from '@/app/common/api/config';
import { useGenerateTokenMutation } from '@/app/common/api/socketApi';
import type {
  SerpDataRootState,
  UpdateSerpPayload
} from '@/app/Serp/types/serpApiTypes';

type SerpSocketProps = {
  serp_id?: string;
};

type SerpSocketPublication = {
  data?: UpdateSerpPayload;
};

const SerpSocket = ({ serp_id }: SerpSocketProps) => {
  const dispatch = useDispatch();
  const serpData = useSelector((state: SerpDataRootState) => state.serpData);
  const { step, setStep, setIsSubmitted } = useContext(NewSerpContext);
  const [generateToken] = useGenerateTokenMutation();
  const [token, setToken] = useState<string | null>(null);
  const stepRef = useRef(step);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    const fetchToken = async () => {
      await generateToken({
        id: serp_id ?? '',
        channels: [`personal:#${serp_id}`]
      })
        .unwrap()
        .then(response => {
          setToken(response.data);
        })
        .catch(err => {
          console.error('Error fetching data:', err);
        });
    };

    fetchToken();
  }, []);

  useEffect(() => {
    if (token) {
      const centrifugo = new Centrifuge(webSocketUrl, {
        getToken: async () => {
          return token;
        }
      });

      centrifugo.on('connecting', () => {
        // console.log('Connecting to socket');
      });

      centrifugo.on('connected', () => {
        // console.log('Connected to socket');
      });

      centrifugo.on('disconnected', () => {
        // console.log('Disconnected from socket');
      });

      centrifugo.on('publication', (message: SerpSocketPublication) => {
        if (message) {
          if (
            message?.data?.type === 'domains' &&
            !serpData?.analysis_domains?.domains_suffix
          ) {
            if (stepRef.current === 4) {
              setStep(5);
              setIsSubmitted(false);
              centrifugo.disconnect();
            } else setStep(4);
          }
          if (message?.data?.type === 'pages' && !serpData?.analysis_pages) {
            if (stepRef.current === 3) setStep(4);
            else {
              setStep(5);
              setIsSubmitted(false);
              centrifugo.disconnect();
            }
          }
          if (message?.data) dispatch(updateSerp(message.data));
        }
      });

      centrifugo.connect();
    }
  }, [token]);

  return <></>;
};

export default memo(SerpSocket);
